import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Breadcrumbs,
  Link,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Tooltip,
  Switch,
  MenuItem,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useNavigate } from "react-router-dom";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import {
  getProducts,
  deleteProduct,
  getCategories,
  updateProduct,
  toggleProductActive,
  fileToBase64,
} from "../../utils/storage";

const PAGE_SIZE = 10;

const BADGE_OPTIONS = [
  "Best Seller", "New Arrival", "Industrial Grade",
  "Trending", "Limited Stock", "Featured",
];

const quillBoxSx = {
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  overflow: "hidden",
  backgroundColor: "#fff",
  "& .ql-toolbar": {
    border: "none",
    borderBottom: "1px solid #eee",
    backgroundColor: "#fafafa",
    padding: "6px",
  },
  "& .ql-container": { border: "none", height: "140px" },
  "& .ql-editor": { height: "140px", fontSize: "14px", padding: "12px", overflowY: "auto" },
};

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};

const EMPTY_EDIT = {
  name: "", brand: "", badge: "", shortDescription: "",
  description: "", highlights: "",
  mrp: "", salePrice: "", discount: "",
  availability: "", warranty: "",
  metaTitle: "", metaDescription: "", seoUrl: "",
};

const Products = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterCat, setFilterCat] = useState(null);
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  // Edit state
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [editErrors, setEditErrors] = useState({});

  // Image state for edit
  // existingImages: array of current base64 strings (up to 3)
  // newImageFiles: { image1, image2, image3 } — null means "keep existing"
  // newImagePreviews: { image1, image2, image3 } — preview of newly chosen file
  const [existingImages, setExistingImages] = useState([null, null, null]);
  const [newImageFiles, setNewImageFiles] = useState({ image1: null, image2: null, image3: null });
  const [newImagePreviews, setNewImagePreviews] = useState({ image1: null, image2: null, image3: null });

  const load = () => {
    setProducts(getProducts());
    setCategories(getCategories());
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const base = filterCat ? products.filter((p) => p.categoryId === filterCat.id) : products;
    setFiltered(base);
    setPage(1);
  }, [filterCat, products]);

  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  // ── Delete ──
  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteProduct(deleteTarget.id);
    setDeleteTarget(null);
    load();
    setSnack({ open: true, message: `"${deleteTarget.name}" deleted.`, severity: "success" });
  };

  // ── Toggle Active ──
  const handleToggleActive = (product) => {
    toggleProductActive(product.id);
    load();
    setSnack({
      open: true,
      message: `"${product.name}" ${product.isActive !== false ? "deactivated" : "activated"}.`,
      severity: "info",
    });
  };

  // ── Edit ──
  const openEdit = (product) => {
    setEditTarget(product);
    setEditForm({
      name: product.name || "",
      brand: product.brand || "",
      badge: product.badge || "",
      shortDescription: product.shortDescription || "",
      description: product.description || "",
      highlights: product.highlights || "",
      mrp: product.mrp || "",
      salePrice: product.salePrice || "",
      discount: product.discount || "",
      availability: product.availability || "",
      warranty: product.warranty || "",
      metaTitle: product.metaTitle || "",
      metaDescription: product.metaDescription || "",
      seoUrl: product.seoUrl || "",
    });
    // Populate existing images (pad to 3 slots)
    const imgs = product.images || [];
    setExistingImages([imgs[0] || null, imgs[1] || null, imgs[2] || null]);
    setNewImageFiles({ image1: null, image2: null, image3: null });
    setNewImagePreviews({ image1: null, image2: null, image3: null });
    setEditErrors({});
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
    setEditErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleImageChange = (e, key, index) => {
    const file = e.target.files[0];
    let error = "";
    if (file) {
      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type))
        error = "Only JPG, PNG, WEBP allowed";
      else if (file.size > 2 * 1024 * 1024)
        error = "Max file size is 2MB";
    }
    if (error) {
      setEditErrors((prev) => ({ ...prev, [key]: error }));
      return;
    }
    setNewImageFiles((prev) => ({ ...prev, [key]: file || null }));
    setEditErrors((prev) => ({ ...prev, [key]: "" }));
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) =>
        setNewImagePreviews((prev) => ({ ...prev, [key]: ev.target.result }));
      reader.readAsDataURL(file);
    }
  };

  const validateEdit = () => {
    const temp = {};
    if (!editForm.name.trim()) temp.name = "Product name is required";
    else if (editForm.name.trim().length < 3) temp.name = "Minimum 3 characters";
    if (!editForm.brand.trim()) temp.brand = "Brand is required";
    if (!editForm.badge) temp.badge = "Badge is required";
    if (!editForm.shortDescription.trim()) temp.shortDescription = "Short description is required";
    else if (editForm.shortDescription.trim().length < 10) temp.shortDescription = "Minimum 10 characters required";
    else if (editForm.shortDescription.trim().length > 400) temp.shortDescription = "Maximum 400 characters allowed";

    const plainDesc = editForm.description.replace(/<(.|\n)*?>/g, "").trim();
    if (!plainDesc) temp.description = "Description is required";
    else if (plainDesc.length < 20) temp.description = "Minimum 20 characters required";
    else if (plainDesc.length > 1000) temp.description = "Maximum 1000 characters allowed";

    const plainHL = editForm.highlights.replace(/<(.|\n)*?>/g, "").trim();
    if (!plainHL) temp.highlights = "Highlights are required";
    else if (plainHL.length < 10) temp.highlights = "Minimum 10 characters required";
    else if (plainHL.length > 100) temp.highlights = "Maximum 100 characters allowed";

    if (!editForm.mrp.trim()) temp.mrp = "MRP is required";
    else if (!/^\d+(\.\d{1,2})?$/.test(editForm.mrp)) temp.mrp = "Enter valid price";
    if (!editForm.salePrice.trim()) temp.salePrice = "Sale price is required";
    else if (!/^\d+(\.\d{1,2})?$/.test(editForm.salePrice)) temp.salePrice = "Enter valid price";
    else if (Number(editForm.salePrice) > Number(editForm.mrp)) temp.salePrice = "Cannot exceed MRP";
    if (!editForm.discount.trim()) temp.discount = "Discount is required";
    else if (!/^\d+$/.test(editForm.discount)) temp.discount = "Numbers only";
    if (!editForm.availability.trim()) temp.availability = "Availability is required";
    if (!editForm.warranty.trim()) temp.warranty = "Warranty is required";
    if (!editForm.metaTitle.trim()) temp.metaTitle = "Meta title is required";
    else if (editForm.metaTitle.length > 60) temp.metaTitle = "Max 60 characters";
    if (!editForm.metaDescription.trim()) temp.metaDescription = "Meta description is required";
    else if (editForm.metaDescription.length > 160) temp.metaDescription = "Max 160 characters";
    if (!editForm.seoUrl.trim()) temp.seoUrl = "SEO URL is required";
    else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(editForm.seoUrl)) temp.seoUrl = "Lowercase, numbers and hyphens only";

    // Each slot must have either an existing image or a new file chosen
    ["image1", "image2", "image3"].forEach((key, i) => {
      if (!existingImages[i] && !newImageFiles[key])
        temp[key] = `Image ${i + 1} is required`;
    });

    setEditErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleEditSave = async () => {
    if (!validateEdit()) return;

    // Resolve final images: new file takes priority, otherwise keep existing
    const keys = ["image1", "image2", "image3"];
    const resolvedImages = await Promise.all(
      keys.map(async (key, i) => {
        if (newImageFiles[key]) {
          return await fileToBase64(newImageFiles[key]);
        }
        return existingImages[i] || null;
      })
    );

    updateProduct(editTarget.id, {
      name: editForm.name.trim(),
      brand: editForm.brand.trim(),
      badge: editForm.badge,
      shortDescription: editForm.shortDescription.trim(),
      description: editForm.description,
      highlights: editForm.highlights,
      mrp: editForm.mrp.trim(),
      salePrice: editForm.salePrice.trim(),
      discount: editForm.discount.trim(),
      availability: editForm.availability.trim(),
      warranty: editForm.warranty.trim(),
      metaTitle: editForm.metaTitle.trim(),
      metaDescription: editForm.metaDescription.trim(),
      seoUrl: editForm.seoUrl.trim(),
      images: resolvedImages.filter(Boolean),
    });
    setEditTarget(null);
    load();
    setSnack({ open: true, message: `"${editForm.name.trim()}" updated successfully.`, severity: "success" });
  };

  const stockColor = (val) => {
    const v = val?.toLowerCase() || "";
    if (v === "in stock") return "success";
    if (v === "out of stock") return "error";
    return "warning";
  };

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f7fb", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" fontWeight="600" color="#333">Product List</Typography>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
          <Link underline="hover" color="inherit" href="/admin/dashboard">Dashboard</Link>
          <Typography color="text.primary">Product List</Typography>
        </Breadcrumbs>
      </Box>

      {/* Main Card */}
      <Paper elevation={2} sx={{ borderRadius: 3, p: 4, backgroundColor: "#fff", minHeight: "500px" }}>
        {/* Card Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4, flexWrap: "wrap", gap: 2 }}>
          <Typography variant="h6" fontWeight="600">
            All Products{" "}
            <Chip label={products.length} size="small" color="primary" sx={{ ml: 1, fontWeight: 700 }} />
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Autocomplete
              sx={{ width: 220 }}
              options={categories}
              getOptionLabel={(o) => o?.name || ""}
              value={filterCat}
              onChange={(_, v) => setFilterCat(v)}
              renderInput={(params) => <TextField {...params} label="Filter by Category" size="small" />}
            />
            <Button
              variant="contained" startIcon={<AddIcon />}
              onClick={() => navigate("/admin/addProducts")}
              sx={{ borderRadius: 2, px: 2, py: 1, textTransform: "uppercase", fontWeight: "500", boxShadow: "none" }}
            >
              Add Product
            </Button>
          </Box>
        </Box>

        {/* Table */}
        {filtered.length === 0 ? (
          <Box sx={{ height: "280px", display: "flex", justifyContent: "center", alignItems: "center", borderRadius: 2, border: "1px dashed #dcdcdc", backgroundColor: "#fafafa" }}>
            <Typography variant="h6" color="text.secondary">No products found.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#1976d2" }}>
                  {["#", "Image", "Product", "Category", "Brand", "Badge", "Stock", "Status", "Actions"].map((h) => (
                    <TableCell key={h} sx={{ color: "#fff", fontWeight: "bold", whiteSpace: "nowrap" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.map((product, idx) => {
                  const isActive = product.isActive !== false;
                  return (
                    <TableRow key={product.id} hover sx={{ opacity: isActive ? 1 : 0.55 }}>
                      <TableCell sx={{ color: "#777", fontSize: "13px" }}>{(page - 1) * PAGE_SIZE + idx + 1}</TableCell>
                      <TableCell>
                        <Avatar
                          src={product.images?.[0]}
                          variant="rounded"
                          sx={{ width: 48, height: 48, objectFit: "contain", bgcolor: "#f0f0f0" }}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: "160px" }}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "160px" }}>
                          {product.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={categories.find((c) => c.id === product.categoryId)?.name || "—"}
                          size="small" variant="outlined" sx={{ fontSize: "11px" }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: "13px" }}>{product.brand}</TableCell>
                      <TableCell>
                        <Chip label={product.badge} size="small" color="info" />
                      </TableCell>
                      <TableCell>
                        <Chip label={product.availability} size="small" color={stockColor(product.availability)} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={isActive ? "Active" : "Inactive"}
                          size="small"
                          color={isActive ? "success" : "default"}
                          variant={isActive ? "filled" : "outlined"}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Tooltip title="Edit Product">
                            <IconButton size="small" color="primary" onClick={() => openEdit(product)}>
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={isActive ? "Deactivate (hide from frontend)" : "Activate"}>
                            <Switch
                              size="small"
                              checked={isActive}
                              onChange={() => handleToggleActive(product)}
                              color="success"
                            />
                          </Tooltip>
                          <Tooltip title="Delete Product">
                            <IconButton size="small" color="error" onClick={() => setDeleteTarget(product)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filtered.length} result{filtered.length !== 1 ? "s" : ""}
          </Typography>
          {totalPages > 1 && (
            <Stack spacing={2}>
              <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" shape="circular" />
            </Stack>
          )}
        </Box>
      </Paper>

      {/* ── Edit Product Dialog ── */}
      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} maxWidth="md" fullWidth>
        <DialogTitle fontWeight={700}>Edit Product</DialogTitle>
        <DialogContent sx={{ pt: "16px !important" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 0.5 }}>

            {/* Name + Brand */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                sx={{ flex: 1, minWidth: "220px" }}
                label="Product Name *" name="name" size="small"
                value={editForm.name} onChange={handleEditChange}
                error={!!editErrors.name} helperText={editErrors.name}
              />
              <TextField
                sx={{ flex: 1, minWidth: "220px" }}
                label="Brand *" name="brand" size="small"
                value={editForm.brand} onChange={handleEditChange}
                error={!!editErrors.brand} helperText={editErrors.brand}
              />
            </Box>

            {/* Badge + Availability + Warranty */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                select sx={{ flex: 1, minWidth: "180px" }}
                label="Badge *" name="badge" size="small"
                value={editForm.badge} onChange={handleEditChange}
                error={!!editErrors.badge} helperText={editErrors.badge}
              >
                {BADGE_OPTIONS.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
              </TextField>
              <TextField
                sx={{ flex: 1, minWidth: "180px" }}
                label="Availability *" name="availability" size="small"
                value={editForm.availability} onChange={handleEditChange}
                error={!!editErrors.availability}
                helperText={editErrors.availability || "In Stock / Out of Stock / Limited Stock"}
              />
              <TextField
                sx={{ flex: 1, minWidth: "180px" }}
                label="Warranty *" name="warranty" size="small"
                value={editForm.warranty} onChange={handleEditChange}
                error={!!editErrors.warranty} helperText={editErrors.warranty}
              />
            </Box>

            {/* Short Description */}
            <TextField
              fullWidth label="Short Description *" name="shortDescription"
              multiline rows={2}
              value={editForm.shortDescription} onChange={handleEditChange}
              error={!!editErrors.shortDescription}
              helperText={editErrors.shortDescription || `${editForm.shortDescription.trim().length}/400`}
            />

            {/* Description */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} mb={0.5} color="#333">
                Description *
              </Typography>
              <Box sx={quillBoxSx}>
                <ReactQuill
                  theme="snow"
                  modules={quillModules}
                  value={editForm.description}
                  onChange={(v) => {
                    setEditForm((prev) => ({ ...prev, description: v }));
                    setEditErrors((prev) => ({ ...prev, description: "" }));
                  }}
                />
              </Box>
              {editErrors.description && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                  {editErrors.description}
                </Typography>
              )}
            </Box>

            {/* Highlights */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} mb={0.5} color="#333">
                Highlights *
              </Typography>
              <Box sx={quillBoxSx}>
                <ReactQuill
                  theme="snow"
                  modules={quillModules}
                  value={editForm.highlights}
                  onChange={(v) => {
                    setEditForm((prev) => ({ ...prev, highlights: v }));
                    setEditErrors((prev) => ({ ...prev, highlights: "" }));
                  }}
                />
              </Box>
              {editErrors.highlights && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                  {editErrors.highlights}
                </Typography>
              )}
            </Box>

            {/* Prices */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {[
                { label: "MRP *", name: "mrp" },
                { label: "Sale Price *", name: "salePrice" },
                { label: "Discount (%) *", name: "discount" },
              ].map(({ label, name }) => (
                <TextField
                  key={name}
                  sx={{ flex: 1, minWidth: "160px" }}
                  label={label} name={name} size="small"
                  value={editForm[name]} onChange={handleEditChange}
                  error={!!editErrors[name]} helperText={editErrors[name]}
                />
              ))}
            </Box>

            {/* Meta */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                sx={{ flex: 1, minWidth: "220px" }}
                label="Meta Title *" name="metaTitle" size="small"
                value={editForm.metaTitle} onChange={handleEditChange}
                error={!!editErrors.metaTitle}
                helperText={editErrors.metaTitle || `${editForm.metaTitle.length}/60`}
              />
              <TextField
                sx={{ flex: 1, minWidth: "220px" }}
                label="Meta Description *" name="metaDescription" size="small"
                value={editForm.metaDescription} onChange={handleEditChange}
                error={!!editErrors.metaDescription}
                helperText={editErrors.metaDescription || `${editForm.metaDescription.length}/160`}
              />
            </Box>

            {/* SEO URL */}
            <TextField
              sx={{ maxWidth: "380px" }}
              label="SEO URL *" name="seoUrl" size="small"
              value={editForm.seoUrl} onChange={handleEditChange}
              error={!!editErrors.seoUrl} helperText={editErrors.seoUrl}
            />

            {/* Images */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} mb={1} color="#333">
                Product Images
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {["image1", "image2", "image3"].map((key, i) => {
                  // What to show as preview: new file preview > existing image > nothing
                  const previewSrc = newImagePreviews[key] || existingImages[i] || null;
                  return (
                    <Box
                      key={key}
                      sx={{
                        flex: 1, minWidth: "160px",
                        border: "1px dashed #d0d5dd",
                        borderRadius: 2, p: 1.5,
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <Typography variant="caption" fontWeight={600} display="block" mb={0.5}>
                        Image {i + 1}{existingImages[i] && !newImagePreviews[key] ? " (current)" : ""}
                      </Typography>
                      {previewSrc ? (
                        <Box
                          component="img"
                          src={previewSrc}
                          alt={`Image ${i + 1}`}
                          sx={{
                            width: "100%", height: "100px",
                            objectFit: "contain", mb: 1,
                            borderRadius: 1, background: "#fff",
                            border: "1px solid #eee",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%", height: "100px", mb: 1,
                            borderRadius: 1, background: "#f0f0f0",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">No image</Typography>
                        </Box>
                      )}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        style={{ width: "100%", fontSize: "12px" }}
                        onChange={(e) => handleImageChange(e, key, i)}
                      />
                      {editErrors[key] && (
                        <Typography variant="caption" color="error" display="block" mt={0.5}>
                          {editErrors[key]}
                        </Typography>
                      )}
                      {existingImages[i] && (
                        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                          Leave empty to keep current image
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>

          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setEditTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave}>Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Dialog ── */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>Delete Product?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>"{deleteTarget?.name}"</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} variant="filled">{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Products;